import { useEffect, useCallback, useMemo, useState } from 'react';
import { Button, Text, TextField } from '@teachstonellc/component-library';
import { Checkmark, Close } from '@teachstonellc/component-library/dist/icons';
import {
  FieldErrors,
  useForm,
  UseFormRegister,
  UseFormWatch
} from 'react-hook-form';
import messages from './messages';
import i18n from 'helpers/i18n';
import clsx from 'clsx';
import {
  getPolicyParts,
  validatePassword,
  validatePasswordConfirmation
} from 'helpers/passwordValidation';
import VisibilityToggle from 'components/VisibilityToggle';

export interface PasswordFormValues {
  password: string;
  password_confirmation: string;
}

export interface PasswordFieldsProps {
  register: UseFormRegister<PasswordFormValues>;
  watch: UseFormWatch<PasswordFormValues>;
  errors: FieldErrors<PasswordFormValues>;
  firstName: string;
  lastName: string;
  email: string;
  label?: string;
  confirmLabel?: string;
  autocomplete?: 'on' | 'off' | 'new-password' | 'current-password';
  fieldClasses?: string;
  requirementsClassName?: string;
  /** Autofocus the first password field on mount */
  autoFocusPassword?: boolean;
}

export interface PasswordProps {
  /** Label for the first password field */
  label?: string;
  /** Label for the password confirmation field */
  confirmLabel?: string;
  /** Text for the clear button */
  clearButtonText?: string;
  /** Text for the submit button */
  submitButtonText?: string;
  /** Autocomplete attribute for the password fields */
  autocomplete?: 'on' | 'off' | 'new-password' | 'current-password';
  /** Callback function to handle form submission */
  onSubmit: (data: PasswordFormValues) => void;
  /** Whether the form was successfully submitted, used to reset form on success */
  isSubmitSuccessful?: boolean;
  /** Whether the form submit request is in progress */
  isSubmitting?: boolean;
  /** Additional classes for the field container, ex: "grid grid-cols-2 gap-4" for a two-column layout */
  fieldClasses?: string;
  /** Optional classes on the root form element */
  className?: string;
  /** Optional classes on the password requirements block */
  requirementsClassName?: string;
  /** First name of the current user, used for validation */
  firstName: string;
  /** Last name of the current user, used for validation */
  lastName: string;
  /** Email of the current user, used for validation */
  email: string;
  /** Optional children to render below the password requirements and above the buttons, ex: TOS checkbox for set password flow */
  children?: React.ReactNode;
  /** Autofocus the first password field on mount */
  autoFocusPassword?: boolean;
}

function fieldErrorMessage(
  error: { message?: string } | undefined,
  fallback: string
): string | undefined {
  if (!error) {
    return undefined;
  }
  if (typeof error.message === 'string' && error.message.length > 0) {
    return error.message;
  }
  return fallback;
}

export const Password = ({
  label = i18n.ft(messages.changePassword),
  confirmLabel = i18n.ft(messages.confirmPassword),
  clearButtonText = i18n.ft(messages.clear),
  submitButtonText = i18n.ft(messages.savePassword),
  autocomplete = 'new-password',
  onSubmit,
  fieldClasses,
  isSubmitSuccessful,
  isSubmitting = false,
  className,
  requirementsClassName,
  firstName,
  lastName,
  email,
  children,
  autoFocusPassword = false
}: PasswordProps): JSX.Element => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<PasswordFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className={className}>
      <PasswordFields
        register={register}
        watch={watch}
        errors={errors}
        label={label}
        confirmLabel={confirmLabel}
        autocomplete={autocomplete}
        fieldClasses={fieldClasses}
        requirementsClassName={requirementsClassName}
        firstName={firstName}
        lastName={lastName}
        email={email}
        autoFocusPassword={autoFocusPassword}
      />

      {children}

      <div className="flex items-center justify-end gap-4">
        <Button
          type="button"
          shape="rounded"
          variant="secondary"
          onClick={() => reset()}
          disabled={isSubmitting}
        >
          {clearButtonText}
        </Button>
        <Button type="submit" shape="rounded" loading={isSubmitting}>
          {isSubmitting ? i18n.ft(messages.saving) : submitButtonText}
        </Button>
      </div>
    </form>
  );
};

export const PasswordFields = ({
  register,
  watch,
  errors,
  firstName,
  lastName,
  email,
  label = i18n.ft(messages.changePassword),
  confirmLabel = i18n.ft(messages.confirmPassword),
  autocomplete = 'new-password',
  fieldClasses,
  requirementsClassName,
  autoFocusPassword = false
}: PasswordFieldsProps): JSX.Element => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const password = watch('password', '');
  const confirmPassword = watch('password_confirmation', '');

  const emailParts = useMemo(() => getPolicyParts(email), [email]);

  const validate = useCallback(
    (value: string) => validatePassword(value, { firstName, lastName, email }),
    [firstName, lastName, email]
  );

  const passwordFieldError = fieldErrorMessage(
    errors.password,
    i18n.ft(messages.passwordDoesNotMeet)
  );

  const confirmFieldError = fieldErrorMessage(
    errors.password_confirmation,
    i18n.ft(messages.passwordsDoNotMatch)
  );

  const validationRules = [
    {
      id: 'length-min',
      label: i18n.ft(messages.lengthMin),
      isValid: password.length >= 12
    },
    {
      id: 'length-max',
      label: i18n.ft(messages.lengthMax),
      isValid: password.length <= 64
    },
    {
      id: 'no-first-name',
      label: i18n.ft(messages.noFirstName),
      isValid:
        firstName === ''
          ? undefined
          : !password.toLowerCase().includes(firstName.toLowerCase())
    },
    {
      id: 'no-last-name',
      label: i18n.ft(messages.noLastName),
      isValid:
        lastName === ''
          ? undefined
          : !password.toLowerCase().includes(lastName.toLowerCase())
    },
    {
      id: 'no-email',
      label: i18n.ft(messages.noEmail),
      isValid:
        emailParts.length === 0
          ? undefined
          : !emailParts.some(part => password.toLowerCase().includes(part))
    },
    {
      id: 'match',
      label: i18n.ft(messages.match),
      isValid: password !== '' && password === confirmPassword
    }
  ];

  const passwordErrorId = errors.password ? 'password-error' : undefined;
  const passwordConfirmationErrorId = errors.password_confirmation
    ? 'password-confirmation-error'
    : undefined;

  return (
    <>
      <div className={fieldClasses || 'grid grid-cols-1 gap-4'}>
        <div
          className={clsx(
            'flex gap-1',
            passwordFieldError ? 'items-start' : 'items-end'
          )}
        >
          <div className="flex-1">
            <TextField
              {...register('password', {
                validate
              })}
              type={showPassword ? 'text' : 'password'}
              label={label}
              autoComplete={autocomplete}
              autoFocus={autoFocusPassword}
              aria-describedby={
                passwordErrorId ? `${passwordErrorId}` : 'password-requirements'
              }
              aria-invalid={!!errors.password}
              error={passwordFieldError}
              errorId={passwordErrorId}
            />
          </div>

          <div className={clsx(passwordFieldError ? 'mt-[28px]' : '')}>
            <VisibilityToggle
              isShown={showPassword}
              showLabel={i18n.ft(messages.showPassword)}
              hideLabel={i18n.ft(messages.hidePassword)}
              onToggle={() => setShowPassword(prev => !prev)}
            />
          </div>
        </div>

        <div
          className={clsx(
            'flex gap-1',
            confirmFieldError ? 'items-start' : 'items-end'
          )}
        >
          <div className="flex-1">
            <TextField
              {...register('password_confirmation', {
                validate: value => validatePasswordConfirmation(value, password)
              })}
              type={showConfirmPassword ? 'text' : 'password'}
              label={confirmLabel}
              autoComplete={autocomplete}
              aria-describedby={
                passwordConfirmationErrorId
                  ? `${passwordConfirmationErrorId}`
                  : 'password-requirements'
              }
              aria-invalid={!!errors.password_confirmation}
              error={confirmFieldError}
              errorId={passwordConfirmationErrorId}
            />
          </div>

          <div className={clsx(confirmFieldError ? 'mt-[28px]' : '')}>
            <VisibilityToggle
              isShown={showConfirmPassword}
              showLabel={i18n.ft(messages.showConfirmPassword)}
              hideLabel={i18n.ft(messages.hideConfirmPassword)}
              onToggle={() => setShowConfirmPassword(prev => !prev)}
            />
          </div>
        </div>
      </div>

      <div
        className={clsx(
          'rounded-lg border border-ds-border bg-ds-surface-subtle p-4 my-4',
          requirementsClassName
        )}
      >
        <Text as="p" variant="label1" className="text-ds-text-subtle mb-3">
          {i18n.ft(messages.passwordRequirementsHeading)}
        </Text>
        <ul className="space-y-2" id="password-requirements">
          {validationRules.map(rule => {
            const isPending =
              (rule.id === 'no-first-name' && firstName === '') ||
              (rule.id === 'no-last-name' && lastName === '') ||
              (rule.id === 'no-email' && emailParts.length === 0);
            const showState = password !== '' && !isPending;
            const rowTone = showState
              ? rule.isValid
                ? 'text-ds-text-positive'
                : 'text-ds-text-negative'
              : 'text-ds-text-subtle';

            return (
              <li
                key={rule.id}
                className={`flex items-center gap-2 text-ds-xs ${rowTone}`}
              >
                <span
                  className="flex h-4 w-4 shrink-0 items-center justify-center"
                  aria-hidden
                >
                  {showState ? (
                    rule.isValid ? (
                      <Checkmark size={16} />
                    ) : (
                      <Close size={16} />
                    )
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-ds-border" />
                  )}
                </span>
                <span>{rule.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};
